/**
 * Helpers for including filter state in analytics events.
 * Kept separate so the logic can evolve independently of UI components.
 */

interface Filters {
  q: string;
  location: { city: string; region: string };
  services: string[];
  priceRange: [number, number];
  availability: { onlineNow: boolean; verified: boolean; newProfile: boolean };
  attrs: Record<string, unknown>;
}

export function hasActiveFilters(filters: Filters): boolean {
  return (
    filters.q.trim() !== '' ||
    filters.location.city.trim() !== '' ||
    filters.location.region.trim() !== '' ||
    filters.services.length > 0 ||
    filters.priceRange[0] > 0 ||
    filters.priceRange[1] < 1000 ||
    filters.availability.verified ||
    filters.availability.newProfile ||
    filters.availability.onlineNow ||
    Object.keys(filters.attrs).length > 0
  );
}

/** Count of currently active (non-default) filters, for badge display. */
export function activeFilterCount(filters: Filters): number {
  let count = 0;
  if (filters.q.trim() !== '') count++;
  if (filters.location.city.trim() !== '') count++;
  if (filters.location.region.trim() !== '') count++;
  count += filters.services.length;
  if (filters.priceRange[0] > 0) count++;
  if (filters.priceRange[1] < 1000) count++;
  if (filters.availability.verified) count++;
  if (filters.availability.newProfile) count++;
  if (filters.availability.onlineNow) count++;
  count += Object.keys(filters.attrs).length;
  return count;
}

/** Returns only the non-default filter values for compact metadata storage. */
export function activeFilterSummary(filters: Filters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (filters.q.trim()) out.q = filters.q.trim();
  if (filters.location.city.trim()) out.city = filters.location.city;
  if (filters.location.region.trim()) out.region = filters.location.region;
  if (filters.services.length) out.services = filters.services;
  if (filters.priceRange[0] > 0) out.price_min = filters.priceRange[0];
  if (filters.priceRange[1] < 1000) out.price_max = filters.priceRange[1];
  if (filters.availability.verified) out.verified = true;
  if (filters.availability.newProfile) out.new_profile = true;
  if (filters.availability.onlineNow) out.online_now = true;
  if (Object.keys(filters.attrs).length) out.attrs = filters.attrs;
  return out;
}
